import { api } from './api.ts'

/**
 * Odber upozorneni na akce. Funguje jen s nainstalovanym service workerem —
 * ve vyvoji (npm run dev) zadny neni, takze se tvari jako nepodporovane.
 * Na iPhonu dorucuje Safari upozorneni jen appce pridane na plochu.
 */

export type PushState = 'unsupported' | 'denied' | 'off' | 'on'

function supported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null
  return (await navigator.serviceWorker.getRegistration()) ?? null
}

/** Klic VAPID je base64url; pushManager ho chce jako bajty. */
function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

export async function pushState(): Promise<PushState> {
  const reg = await registration()
  if (!reg) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  return (await reg.pushManager.getSubscription()) ? 'on' : 'off'
}

export async function enablePush(): Promise<PushState> {
  const reg = await registration()
  if (!reg) return 'unsupported'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const { publicKey } = await api.push.key()
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }))
  const json = subscription.toJSON()
  await api.push.subscribe({
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.['p256dh'] ?? '', auth: json.keys?.['auth'] ?? '' },
  })
  return 'on'
}

export async function disablePush(): Promise<PushState> {
  const reg = await registration()
  if (!reg) return 'unsupported'
  const subscription = await reg.pushManager.getSubscription()
  if (subscription) {
    // Odhlaseni na serveru muze bez signalu selhat; prohlizec odber zrusi i tak
    // a server prosly odber smaze pri pristim odesilani (410).
    await api.push.unsubscribe(subscription.endpoint).catch(() => undefined)
    await subscription.unsubscribe()
  }
  return 'off'
}
